// =============================================================================
// PEDRA — src/lib/pdfSvg.ts
// Leva o SVG do diagrama (o MESMO que o <SheetDiagram> desenha na tela) para
// uma pagina do PDF, em vetor: retangulos, linhas, circulos, caminhos simples,
// texto e hachuras.
//
// POR QUE ler o SVG em vez de redesenhar a chapa a partir dos dados: o
// diagrama tem regras finas (rotulo em niveis, cotas na margem, fonte que so
// encolhe nas sobras). Duplicar isso aqui seria garantir que o PDF e a tela
// um dia discordem. Lendo o SVG, o papel sai igual ao que a pessoa conferiu.
//
// AS CORES NAO VEM DO CSS DA TELA: o diagrama pinta quase tudo por classe, e
// no tema escuro a chapa e grafite. Papel e sempre claro, entao as classes do
// diagrama tem aqui uma paleta propria — a do tema claro (theme.css), com
// tracos em espessura de papel no lugar dos `non-scaling-stroke` da tela.
// So a cor que vem do DADO (peca, fita) e lida do proprio atributo `fill`.
//
// Suporta o subconjunto que o diagrama usa: <g transform>, <rect rx>, <line>,
// <circle>, <path> com M/L/H/V/Z, <text> com text-anchor e dominant-baseline,
// e <pattern patternUnits="userSpaceOnUse"> como preenchimento.
// =============================================================================

import type { PdfColor, PdfFont, PdfMatrix, PdfPage } from './pdf';
import { IDENTITY_MATRIX, isEncodable, multiply, parseColor } from './pdf';

// -----------------------------------------------------------------------------
// PALETA DE PAPEL
// -----------------------------------------------------------------------------

/** Tinta usada quando nem classe nem atributo dizem a cor. */
const INK: PdfColor = [0.12, 0.16, 0.2];

/** Espelho do tema claro de theme.css — so o que o diagrama usa. */
const PAPER = {
  sheetBg: '#ffffff',
  sheetBorder: '#3a434d',
  wasteHatch: '#b9c2cc',
  offcutHatch: '#8fbfac',
  borderStrong: '#b9c3ce',
  success: '#15704e',
  textMuted: '#55616e',
  partStroke: '#4b5665',
  partText: '#1f2933',
  cutLine: '#6e7a88',
  dimensionLine: '#7a8794',
  bgElevated: '#ffffff',
} as const;

type Paint =
  | { kind: 'none' }
  | { kind: 'color'; color: PdfColor }
  | { kind: 'pattern'; id: string };

interface Style {
  fill: Paint;
  stroke: Paint;
  /** Espessura do traco em PONTOS de papel (independe da escala). */
  strokeWidth: number;
  /** Tracejado em pontos de papel; vazio = continuo. */
  dash: readonly number[];
  font: PdfFont;
}

/** Parte do estilo que uma regra de classe pode definir. */
type StylePatch = Partial<Omit<Style, 'fill' | 'stroke'>> & { fill?: string; stroke?: string };

const NONE: Paint = { kind: 'none' };

const DEFAULT_STYLE: Style = {
  fill: { kind: 'color', color: INK },
  stroke: NONE,
  strokeWidth: 0.5,
  dash: [],
  font: 'sans',
};

/**
 * Regras por classe, na ordem em que sao aplicadas (a ultima vence). `inside`
 * exige que algum ANCESTRAL tenha aquela classe — o equivalente ao seletor
 * descendente do CSS (`.diagram-offcut.is-usable .diagram-offcut-border`).
 */
const CLASS_RULES: ReadonlyArray<{ cls: string; inside?: string; style: StylePatch }> = [
  { cls: 'diagram-sheet', style: { fill: PAPER.sheetBg } },
  { cls: 'diagram-sheet-border', style: { fill: 'none', stroke: PAPER.sheetBorder, strokeWidth: 1 } },
  { cls: 'diagram-hatch-waste', style: { fill: 'none', stroke: PAPER.wasteHatch, strokeWidth: 0.45 } },
  { cls: 'diagram-hatch-offcut', style: { fill: 'none', stroke: PAPER.offcutHatch, strokeWidth: 0.45 } },
  { cls: 'diagram-offcut-border', style: { stroke: PAPER.borderStrong, strokeWidth: 0.5, dash: [3, 2] } },
  { cls: 'diagram-offcut-border', inside: 'is-usable', style: { stroke: PAPER.success } },
  { cls: 'diagram-offcut-text', style: { fill: PAPER.textMuted, font: 'sans' } },
  { cls: 'diagram-part-rect', style: { stroke: PAPER.partStroke, strokeWidth: 0.6 } },
  { cls: 'diagram-part-text', style: { fill: PAPER.partText, font: 'sansBold' } },
  { cls: 'is-dims', style: { font: 'sans' } },
  { cls: 'diagram-part-glyph', style: { fill: PAPER.partText } },
  { cls: 'diagram-band', style: { stroke: 'none' } },
  { cls: 'diagram-cut-line', style: { stroke: PAPER.cutLine, strokeWidth: 0.5, dash: [4, 2.5] } },
  { cls: 'diagram-cut-badge', style: { fill: PAPER.bgElevated, stroke: PAPER.cutLine, strokeWidth: 0.5 } },
  { cls: 'diagram-cut-num', style: { fill: PAPER.textMuted, font: 'sans' } },
  { cls: 'diagram-dim-line', style: { stroke: PAPER.dimensionLine, strokeWidth: 0.5 } },
  { cls: 'diagram-dim-tick', style: { stroke: PAPER.dimensionLine, strokeWidth: 0.5 } },
  { cls: 'diagram-dim-bg', style: { fill: PAPER.sheetBg } },
  { cls: 'diagram-dim-text', style: { fill: PAPER.dimensionLine, font: 'sans' } },
];

function toPaint(value: string | null | undefined): Paint | null {
  if (value === null || value === undefined) return null;
  const v = value.trim();
  if (v === '') return null;
  if (v === 'none' || v === 'transparent') return NONE;
  const ref = /^url\(\s*['"]?#([^'")\s]+)['"]?\s*\)$/.exec(v);
  if (ref) return { kind: 'pattern', id: ref[1] as string };
  const color = parseColor(v);
  return color ? { kind: 'color', color } : null;
}

function classesOf(el: Element): string[] {
  const raw = el.getAttribute('class');
  return raw ? raw.split(/\s+/).filter(Boolean) : [];
}

/**
 * Estilo final de um elemento: herdado <- atributos de apresentacao <- regras
 * de classe. Como no CSS, a regra de classe vence o atributo `fill="..."`.
 */
function resolveStyle(el: Element, inherited: Style, ancestors: ReadonlySet<string>): Style {
  const style: Style = { ...inherited };

  const fillAttr = toPaint(el.getAttribute('fill'));
  if (fillAttr) style.fill = fillAttr;
  const strokeAttr = toPaint(el.getAttribute('stroke'));
  if (strokeAttr) style.stroke = strokeAttr;

  const own = new Set(classesOf(el));
  for (const rule of CLASS_RULES) {
    if (!own.has(rule.cls)) continue;
    if (rule.inside !== undefined && !ancestors.has(rule.inside)) continue;
    const { fill, stroke, ...rest } = rule.style;
    if (fill !== undefined) style.fill = toPaint(fill) ?? NONE;
    if (stroke !== undefined) style.stroke = toPaint(stroke) ?? NONE;
    Object.assign(style, rest);
  }
  return style;
}

// -----------------------------------------------------------------------------
// ATRIBUTOS
// -----------------------------------------------------------------------------

function numAttr(el: Element, name: string, fallback = 0): number {
  const raw = el.getAttribute(name);
  if (raw === null) return fallback;
  const v = parseFloat(raw);
  return Number.isFinite(v) ? v : fallback;
}

/** Le `transform="translate(..) scale(..) rotate(..) matrix(..)"`. */
export function parseTransform(raw: string | null): PdfMatrix {
  if (!raw) return IDENTITY_MATRIX;
  let m: PdfMatrix = IDENTITY_MATRIX;
  const re = /(matrix|translate|scale|rotate|skewX|skewY)\s*\(([^)]*)\)/g;
  let hit: RegExpExecArray | null;
  while ((hit = re.exec(raw)) !== null) {
    const fn = hit[1] as string;
    const args = (hit[2] as string)
      .split(/[\s,]+/)
      .filter(Boolean)
      .map(Number)
      .filter(Number.isFinite);
    let t: PdfMatrix = IDENTITY_MATRIX;
    if (fn === 'matrix' && args.length === 6) {
      t = args as unknown as PdfMatrix;
    } else if (fn === 'translate') {
      t = [1, 0, 0, 1, args[0] ?? 0, args[1] ?? 0];
    } else if (fn === 'scale') {
      const sx = args[0] ?? 1;
      t = [sx, 0, 0, args[1] ?? sx, 0, 0];
    } else if (fn === 'rotate') {
      const a = ((args[0] ?? 0) * Math.PI) / 180;
      const cos = Math.cos(a);
      const sin = Math.sin(a);
      const cx = args[1] ?? 0;
      const cy = args[2] ?? 0;
      // translate(cx cy) rotate(a) translate(-cx -cy)
      t = [cos, sin, -sin, cos, cx - cos * cx + sin * cy, cy - sin * cx - cos * cy];
    } else if (fn === 'skewX') {
      t = [1, 0, Math.tan(((args[0] ?? 0) * Math.PI) / 180), 1, 0, 0];
    } else if (fn === 'skewY') {
      t = [1, Math.tan(((args[0] ?? 0) * Math.PI) / 180), 0, 1, 0, 0];
    }
    m = multiply(m, t);
  }
  return m;
}

/** Caminho SVG com M/L/H/V/Z (absolutos e relativos) — o que o diagrama usa. */
function tracePath(page: PdfPage, d: string): boolean {
  const tokens = d.match(/[MLHVZmlhvz]|-?(?:\d+\.?\d*|\.\d+)(?:e[-+]?\d+)?/g);
  if (!tokens) return false;
  let i = 0;
  let cmd = '';
  let x = 0;
  let y = 0;
  let startX = 0;
  let startY = 0;
  let drew = false;
  const next = (): number => Number(tokens[i++]);
  while (i < tokens.length) {
    const tok = tokens[i] as string;
    if (/[A-Za-z]/.test(tok)) {
      cmd = tok;
      i++;
      if (cmd === 'Z' || cmd === 'z') {
        page.closePath();
        x = startX;
        y = startY;
      }
      continue;
    }
    const rel = cmd === cmd.toLowerCase();
    switch (cmd.toUpperCase()) {
      case 'M': {
        const nx = next();
        const ny = next();
        x = rel ? x + nx : nx;
        y = rel ? y + ny : ny;
        startX = x;
        startY = y;
        page.moveTo(x, y);
        // Pares seguintes a um M sao L implicitos.
        cmd = rel ? 'l' : 'L';
        break;
      }
      case 'L': {
        const nx = next();
        const ny = next();
        x = rel ? x + nx : nx;
        y = rel ? y + ny : ny;
        page.lineTo(x, y);
        drew = true;
        break;
      }
      case 'H': {
        const nx = next();
        x = rel ? x + nx : nx;
        page.lineTo(x, y);
        drew = true;
        break;
      }
      case 'V': {
        const ny = next();
        y = rel ? y + ny : ny;
        page.lineTo(x, y);
        drew = true;
        break;
      }
      default:
        // Comando nao suportado (curva, arco): para aqui sem quebrar o resto.
        return drew;
    }
  }
  return drew;
}

// -----------------------------------------------------------------------------
// DESENHO
// -----------------------------------------------------------------------------

interface WalkContext {
  page: PdfPage;
  /** Quantos pontos de papel vale uma unidade do espaco atual. */
  scale: number;
  patterns: ReadonlyMap<string, Element>;
  style: Style;
  ancestors: ReadonlySet<string>;
}

/** Aplica cor e espessura do traco; `false` quando nao ha traco a fazer. */
function applyStroke(ctx: WalkContext, style: Style): boolean {
  if (style.stroke.kind !== 'color' || style.strokeWidth <= 0) return false;
  const k = 1 / (ctx.scale || 1);
  ctx.page
    .strokeColor(style.stroke.color)
    .lineWidth(style.strokeWidth * k)
    .dash(style.dash.map((v) => v * k));
  return true;
}

/**
 * Pinta o caminho que `trace` descreve. Preenchimento por padrao e traco
 * precisam de dois caminhos, porque `scn f` consome o primeiro.
 */
function paint(ctx: WalkContext, style: Style, trace: () => void, fillable = true): void {
  const page = ctx.page;
  const fill = fillable ? style.fill : NONE;

  if (fill.kind === 'pattern') {
    const pattern = ctx.patterns.get(fill.id);
    if (pattern) {
      page.save();
      trace();
      drawPatternFill(ctx, pattern);
      page.restore();
    }
    if (applyStroke(ctx, style)) {
      trace();
      page.stroke();
    }
    return;
  }

  const hasFill = fill.kind === 'color';
  if (hasFill) page.fillColor(fill.color);
  const hasStroke = applyStroke(ctx, style);
  if (!hasFill && !hasStroke) return;
  trace();
  if (hasFill && hasStroke) page.fillAndStroke();
  else if (hasFill) page.fill();
  else page.stroke();
}

function drawPatternFill(ctx: WalkContext, pattern: Element): void {
  const w = numAttr(pattern, 'width');
  const h = numAttr(pattern, 'height');
  if (w <= 0 || h <= 0) {
    ctx.page.clip();
    return;
  }
  const ancestors = new Set([...ctx.ancestors, ...classesOf(pattern)]);
  ctx.page.fillPattern(w, h, (cell) => {
    const cellCtx: WalkContext = { ...ctx, page: cell, style: DEFAULT_STYLE, ancestors };
    for (const child of Array.from(pattern.children)) drawElement(cellCtx, child);
  });
}

/** Mapeia dominant-baseline / text-anchor do SVG para o escritor de PDF. */
function textPlacement(el: Element): { align: 'left' | 'center' | 'right'; baseline: 'alphabetic' | 'middle' | 'top' } {
  const anchor = el.getAttribute('text-anchor') ?? '';
  const align = anchor === 'middle' ? 'center' : anchor === 'end' ? 'right' : 'left';
  const db = el.getAttribute('dominant-baseline') ?? '';
  const baseline =
    db === 'central' || db === 'middle'
      ? 'middle'
      : db === 'hanging' || db === 'text-before-edge'
        ? 'top'
        : 'alphabetic';
  return { align, baseline };
}

function drawElement(ctx: WalkContext, el: Element): void {
  const tag = el.localName.toLowerCase();
  if (tag === 'title' || tag === 'desc' || tag === 'defs' || tag === 'pattern' || tag === 'style') return;
  if (tag === 'script' || tag === 'foreignobject' || tag === 'svg') return;

  const style = resolveStyle(el, ctx.style, ctx.ancestors);
  const transform = parseTransform(el.getAttribute('transform'));
  const transformed = transform !== IDENTITY_MATRIX;

  const page = ctx.page;
  if (transformed) {
    page.save();
    page.transform(transform);
  }
  const here: WalkContext = transformed
    ? { ...ctx, scale: ctx.scale * Math.sqrt(Math.abs(transform[0] * transform[3] - transform[1] * transform[2])) }
    : ctx;

  switch (tag) {
    case 'g':
    case 'a': {
      const ancestors = new Set([...ctx.ancestors, ...classesOf(el)]);
      const child: WalkContext = { ...here, style, ancestors };
      for (const node of Array.from(el.children)) drawElement(child, node);
      break;
    }
    case 'rect': {
      const x = numAttr(el, 'x');
      const y = numAttr(el, 'y');
      const w = numAttr(el, 'width');
      const h = numAttr(el, 'height');
      if (w <= 0 || h <= 0) break;
      const rx = numAttr(el, 'rx', numAttr(el, 'ry'));
      paint(here, style, () => {
        if (rx > 0) page.roundedRect(x, y, w, h, rx);
        else page.rect(x, y, w, h);
      });
      break;
    }
    case 'line': {
      const x1 = numAttr(el, 'x1');
      const y1 = numAttr(el, 'y1');
      const x2 = numAttr(el, 'x2');
      const y2 = numAttr(el, 'y2');
      paint(here, style, () => page.moveTo(x1, y1).lineTo(x2, y2), false);
      break;
    }
    case 'circle': {
      const r = numAttr(el, 'r');
      if (r <= 0) break;
      const cx = numAttr(el, 'cx');
      const cy = numAttr(el, 'cy');
      paint(here, style, () => page.circle(cx, cy, r));
      break;
    }
    case 'path': {
      const d = el.getAttribute('d');
      if (!d) break;
      paint(here, style, () => {
        tracePath(page, d);
      });
      break;
    }
    case 'text': {
      const value = (el.textContent ?? '').trim();
      // Glifo sem equivalente na fonte do PDF (ex.: ⟳): melhor nada do que "?".
      if (value === '' || !isEncodable(value) || style.fill.kind !== 'color') break;
      const { align, baseline } = textPlacement(el);
      page.text(value, numAttr(el, 'x'), numAttr(el, 'y'), {
        font: style.font,
        size: numAttr(el, 'font-size', 16),
        align,
        baseline,
        color: style.fill.color,
      });
      break;
    }
    default:
      break;
  }

  if (transformed) page.restore();
}

// -----------------------------------------------------------------------------
// ENTRADA
// -----------------------------------------------------------------------------

export interface PdfBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface ViewBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

function viewBoxOf(svg: Element): ViewBox | null {
  const raw = svg.getAttribute('viewBox');
  if (raw) {
    const parts = raw.trim().split(/[\s,]+/).map(Number);
    if (parts.length >= 4 && parts.every(Number.isFinite)) {
      const [x, y, w, h] = parts as [number, number, number, number];
      if (w > 0 && h > 0) return { x, y, w, h };
    }
  }
  const w = numAttr(svg, 'width');
  const h = numAttr(svg, 'height');
  return w > 0 && h > 0 ? { x: 0, y: 0, w, h } : null;
}

/** Proporcao largura/altura do desenho, para escolher a orientacao da pagina. */
export function svgAspect(svg: Element): number {
  const vb = viewBoxOf(svg);
  return vb ? vb.w / vb.h : 1;
}

/**
 * Desenha o SVG dentro de `box`, o maior possivel sem distorcer, centrado na
 * horizontal e encostado no topo. Devolve o retangulo realmente ocupado
 * (`null` quando o SVG nao tem tamanho).
 */
export function drawSvg(page: PdfPage, svg: Element, box: PdfBox): PdfBox | null {
  const vb = viewBoxOf(svg);
  if (!vb || box.width <= 0 || box.height <= 0) return null;

  const s = Math.min(box.width / vb.w, box.height / vb.h);
  const w = vb.w * s;
  const h = vb.h * s;
  const ox = box.x + (box.width - w) / 2;
  const oy = box.y;

  const patterns = new Map<string, Element>();
  svg.querySelectorAll('pattern').forEach((p) => {
    const id = p.getAttribute('id');
    if (id) patterns.set(id, p);
  });

  page.save();
  page.transform([s, 0, 0, s, ox - vb.x * s, oy - vb.y * s]);
  // O SVG recorta no proprio viewBox; o PDF tem de fazer o mesmo.
  page.rect(vb.x, vb.y, vb.w, vb.h).clip();

  const ctx: WalkContext = {
    page,
    scale: s,
    patterns,
    style: DEFAULT_STYLE,
    ancestors: new Set(classesOf(svg)),
  };
  for (const child of Array.from(svg.children)) drawElement(ctx, child);
  page.restore();

  return { x: ox, y: oy, width: w, height: h };
}
